<template>
  <BgImageLayout title="Subscription workflow management">
    <AgGridWrapper ref="wrapper" :options="gridOptions">
      <template #header-row>
        <HeaderRow
          :filters="filters"
          :is-save-btn-disabled="!hasDataToUpdate"
          :is-save-btn-loading="requestInProgress"
          @open-modal="newSubsSettingsModalActive = true"
          @filter="applyFilter"
          @save="onSave"
        />
      </template>
    </AgGridWrapper>

    <component :is="newSubsSettingsModal" v-model="newSubsSettingsModalActive" />
  </BgImageLayout>
</template>

<script>
import * as apiTo from '@api/api-routes'
import SubscriptionWorkflow from './vuex/SubscriptionWorkflow'
import { mapGetters, mapMutations } from 'vuex'
import { cloneDeep } from 'lodash'
import { gridOptions } from './gridOptions'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import HeaderRow from './components/HeaderRow'

export default {
  components: {
    AgGridWrapper,
    HeaderRow,
    NewSubsSettingsModal: () => import('./components/NewSubsSettingsModal')
  },

  mixins: [gridOptions],

  data: () => ({
    filters: ['current'],
    current: null,
    future: null,
    oneTimePurchase: null,
    newSubsSettingsModalActive: false,
    requestInProgress: false,
    areFiltersEnabled: false
  }),

  computed: {
    ...mapGetters('subscription-workflow', ['rowsToUpdate']),

    newSubsSettingsModal() {
      return this.newSubsSettingsModalActive ? 'NewSubsSettingsModal' : null
    },

    rows() {
      /* merging current, future, oneTimePurchase data */
      if (!this.current && !this.future && !this.oneTimePurchase) return null
      return this.filters.reduce((acc, curr) => {
        if (this[curr]) {
          const existingRowsIds = acc.map(row => row.id)
          this[curr].forEach(obj => (obj.belongsTo = curr)) // belongsTo canBe current, future, oneTimePurchase
          const uniqueRows = this[curr].filter(row => !existingRowsIds.includes(row.id))
          acc.push(...uniqueRows)
        }
        return acc
      }, [])
    },

    pinnedTopRowData() {
      return [
        {
          product_type_name: '',
          start_date: '',
          end_date: '',
          publisher_name: '',
          vendor_name: '',
          plan_workflow: 'plan_workflow',
          request_prices: 'request_prices',
          price_entries: 'price_entries',
          stakeholders: 'stakeholders',
          evaluations: 'evaluations',
          decisions: 'decisions',
          place_orders: 'place_orders',
          approve_invoices: 'approve_invoices',
          vendor_payments: 'vendor_payments',
          implementations: 'implementations',
          inform_users: 'inform_users'
        }
      ]
    },

    hasDataToUpdate() {
      const rowsToUpdate = this.rowsToUpdate
      const payload = this.generatePayload(Object.entries(rowsToUpdate))
      return !!Object.keys(payload).length
    }
  },

  watch: {
    rows(newVal, oldVal) {
      this.$nextTick(() => {
        if (this.rows.length) this.$refs.wrapper.setTopPinnedData(...this.pinnedTopRowData)
        else this.$refs.wrapper.setTopPinnedData(null)
        if (oldVal && newVal.length === oldVal.length) {
          // update happened
          const changedRows = this.findChangedRows(newVal, oldVal)
          this.$refs.wrapper.updateRows(changedRows)
        } else {
          // new data inserted to the table
          this.$refs.wrapper.setRows(newVal)
        }
      })
    }
  },

  created() {
    this.$store.registerModule('subscription-workflow', SubscriptionWorkflow)
    this.getFilteredSubs('current')
  },

  beforeDestroy() {
    this.clearState()
    this.$store.unregisterModule('subscription-workflow')
  },

  methods: {
    ...mapMutations('subscription-workflow', ['updateRow', 'clearState']),

    cellParams(params, key) {
      return {
        val: params.value.applicable,
        key,
        onUpdate: (...payload) => {
          this.onCellToggle(payload)
        },
        disable: params.value.disabled,
        disableText: 'This task cannot be changed because it has been either completed or dismissed'
      }
    },

    headerParams(key) {
      return {
        val: this.isColumnToggledToTrue(key),
        key,
        onUpdate: this.toggleColumn.bind(key)
      }
    },

    applyFilter(filter) {
      if (this.filters.includes(filter)) this.filters = this.filters.filter(f => f !== filter)
      else {
        this.filters.push(filter)
        !this[filter] && this.getFilteredSubs(filter)
      }
    },

    findChangedRows(newRows, oldRows) {
      return newRows.reduce((acc, curr) => {
        const oldRow = oldRows.find(row => row.id === curr.id)
        if (JSON.stringify(oldRow) !== JSON.stringify(curr)) acc.push(curr)
        return acc
      }, [])
    },

    getFilteredSubs(filter) {
      apiTo.getFilteresSubscriptions(filter).then(({ data }) => (this[filter] = data.result))
    },

    onCellToggle([toggleState, taskKey, rowId]) {
      const { belongsTo } = this.rows.find(row => row.id === rowId)
      const index = this[belongsTo].findIndex(row => row.id === rowId)
      if (~index) this.toggleCell(belongsTo, taskKey, toggleState, index, rowId)
    },

    toggleRow(newVal, key, rowId) {
      const allTasksFields = this.getAllTasksFields()
      allTasksFields.forEach(task => this.onCellToggle([newVal, task, rowId]))
    },

    toggleCell(dataRows, cellKey, toggleState, rowIndex, rowId) {
      const originalRow = this[dataRows].find(row => row.id === rowId)
      const updatedRow = cloneDeep(originalRow)
      if (updatedRow[cellKey].disabled) return
      updatedRow[cellKey].applicable = toggleState
      if (cellKey === 'approve_invoices' && !toggleState) {
        updatedRow.vendor_payments.applicable = false
        this.rowChanged(rowId, 'vendor_payments', false, originalRow)
      } else if (cellKey === 'vendor_payments' && toggleState) {
        updatedRow.approve_invoices.applicable = true
        this.rowChanged(rowId, 'approve_invoices', true, originalRow)
      }
      this[dataRows].splice(rowIndex, 1, updatedRow)
      this.rowChanged(rowId, cellKey, toggleState, originalRow)
    },

    rowChanged(rowId, cellKey, toggleState, originalRow) {
      this.updateRow({
        id: rowId,
        key: cellKey,
        value: toggleState,
        originalValue: originalRow[cellKey].applicable
      })
    },

    toggleColumn(toggleState, cellKey) {
      const allRows = this.getTableRows()
      allRows.forEach(row => {
        const index = this[row.belongsTo].findIndex(item => item.id === row.id)
        this.toggleCell(row.belongsTo, cellKey, toggleState, index, row.id)
      })
    },

    getTableRows() {
      if (this.areFiltersEnabled) return cloneDeep(this.$refs.wrapper.getAllRowsAfterFilter())
      return cloneDeep(this.rows)
    },

    isRowToggledToTrue(params) {
      const tasksFields = this.getAllTasksFields()
      let tasksToggled = []
      tasksFields.forEach(task => tasksToggled.push(params.data[task].applicable))
      return tasksToggled.every(task => task)
    },

    isColumnToggledToTrue(key) {
      if (!this.rows) return
      return this.rows.every(row => row[key].applicable)
    },

    getAllTasksFields() {
      return [
        'plan_workflow',
        'request_prices',
        'price_entries',
        'stakeholders',
        'evaluations',
        'decisions',
        'place_orders',
        'approve_invoices',
        'vendor_payments',
        'implementations',
        'inform_users'
      ]
    },

    onSave() {
      if (!this.hasDataToUpdate) return
      this.requestInProgress = true
      const rows = this.rowsToUpdate
      apiTo
        .saveSubscriptionWorkflow(this.generatePayload(Object.entries(rows)))
        .then(() => this.clearState())
        .finally(() => (this.requestInProgress = false))
    },

    generatePayload(rowsToUpdate) {
      return rowsToUpdate.reduce((acc, [key, value]) => {
        acc[key] = {}
        for (const [taskName, taskValue] of Object.entries(value)) {
          if (taskValue.value !== taskValue.originalValue) acc[key][taskName] = taskValue.value
        }
        if (!Object.keys(acc[key]).length) delete acc[key]
        return acc
      }, {})
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins'
>>> .table-content {
  height calc(100vh - 150px) !important
}

>>> .ag-theme-material {
  height calc(100% - 50px)
}

>>> .ag-floating-top {
  overflow-y auto !important
}

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');
</style>
